/**
 * MatterNotesTable — the matter's key facts, as a table.
 *
 * Notes were a list of label/value rows: the label left-aligned as the row
 * title, the value right-aligned in a 168px trailing slot. Two columns of text
 * pushed to opposite edges of a wide row is the least readable arrangement
 * there is — the eye has to travel the full width to pair a fact with its
 * value, long values were truncated to nothing, and the type and author were
 * folded into a grey run-on line underneath.
 *
 * A note is a record: Note · Value · Type · Source · Updated. On the shared
 * `Table`, so the value column gets real width, the pairs sit side by side, and
 * every column sorts.
 *
 * SOURCE is best-effort — see `noteModel.noteOrigin`. Where the payload cannot
 * say, the cell renders the standard muted dash rather than picking a side.
 */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PiCalendarLight,
  PiHashLight,
  PiSparkleLight,
  PiTextAaLight,
  PiTextAlignLeftLight,
  PiToggleLeftLight,
  PiUserLight,
} from 'react-icons/pi';
import Table from '../../../shared/TailwindComponents/Table';
import Badge from '../../../shared/TailwindComponents/Badge';
import { getIntlLocale } from '../../../../utils/functions';
import {
  NoteOrigin,
  NoteValueType,
  detectNoteValueType,
  noteAuthor,
  noteOrigin,
  noteStamp,
  noteValueString,
} from './noteModel';

export interface MatterNotesTableProps {
  notes: any[];
  isLoading?: boolean;
  selectedId?: string | null;
  onSelectNote: (note: any) => void;
  /** Hidden when the payload cannot distinguish origins at all. */
  showSource?: boolean;
}

type SortKey = 'note' | 'value' | 'type' | 'source' | 'updated';

const TYPE_ICON: Record<NoteValueType, React.ReactNode> = {
  date: <PiCalendarLight className="h-3.5 w-3.5" aria-hidden />,
  number: <PiHashLight className="h-3.5 w-3.5" aria-hidden />,
  boolean: <PiToggleLeftLight className="h-3.5 w-3.5" aria-hidden />,
  long_text: <PiTextAlignLeftLight className="h-3.5 w-3.5" aria-hidden />,
  short_text: <PiTextAaLight className="h-3.5 w-3.5" aria-hidden />,
};

const TYPE_LABEL: Record<NoteValueType, string> = {
  short_text: 'Text',
  long_text: 'Note',
  date: 'Date',
  number: 'Number',
  boolean: 'Yes / No',
};

const MatterNotesTable: React.FC<MatterNotesTableProps> = ({
  notes,
  isLoading = false,
  selectedId,
  onSelectNote,
  showSource = true,
}) => {
  const { t } = useTranslation();

  const [sortKey, setSortKey] = useState<SortKey>('note');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const NOTE = t('Note');
  const VALUE = t('Value');
  const TYPE = t('Type');
  const SOURCE = t('Source');
  const UPDATED = t('Updated');

  /* `Table` flattens column labels to `lower_snake` for `onSort`/`sortBy`. */
  const flatten = (column: string) => column.toLowerCase().replace(/\s+/g, '_');

  const COLUMN_KEY: Record<string, SortKey> = {
    [flatten(NOTE)]: 'note',
    [flatten(VALUE)]: 'value',
    [flatten(TYPE)]: 'type',
    [flatten(SOURCE)]: 'source',
    [flatten(UPDATED)]: 'updated',
  };

  const KEY_COLUMN: Record<SortKey, string> = {
    note: NOTE, value: VALUE, type: TYPE, source: SOURCE, updated: UPDATED,
  };

  const formatValue = (raw: string, type: NoteValueType): string => {
    if (!raw) return '';
    switch (type) {
      case 'date':
        try {
          return new Date(raw).toLocaleDateString(getIntlLocale(), {
            year: 'numeric', month: 'long', day: '2-digit',
          });
        } catch {
          return raw;
        }
      case 'boolean': {
        const lower = raw.toLowerCase();
        if (lower === 'true' || lower === 'yes') return t('Yes');
        if (lower === 'false' || lower === 'no') return t('No');
        return raw;
      }
      case 'number':
        return Number(raw).toLocaleString();
      default:
        return raw;
    }
  };

  const formatStamp = (raw: string): string => {
    if (!raw) return '';
    try {
      return new Date(raw).toLocaleDateString(getIntlLocale(), {
        year: 'numeric', month: 'short', day: 'numeric',
      });
    } catch {
      return raw;
    }
  };

  /** One reading of a note, so every cell and the sort agree about it. */
  const factsOf = (note: any) => {
    const raw = noteValueString(note?.value);
    const type = (note?.type || detectNoteValueType(raw)) as NoteValueType;
    return {
      id: String(note?.label ?? ''),
      label: String(note?.label ?? ''),
      display: formatValue(raw, type),
      type,
      origin: noteOrigin(note) as NoteOrigin,
      author: noteAuthor(note),
      stamp: noteStamp(note),
    };
  };

  const sorted = useMemo(() => {
    const direction = sortOrder === 'asc' ? 1 : -1;
    return [...notes].sort((a, b) => {
      const left = factsOf(a);
      const right = factsOf(b);
      switch (sortKey) {
        case 'value':
          return left.display.localeCompare(right.display, undefined, { numeric: true }) * direction;
        case 'type':
          return t(TYPE_LABEL[left.type]).localeCompare(t(TYPE_LABEL[right.type])) * direction;
        case 'source':
          return left.origin.localeCompare(right.origin) * direction;
        case 'updated':
          // Undated LAST in both directions — a missing stamp is not "oldest".
          if (!left.stamp && !right.stamp) return 0;
          if (!left.stamp) return 1;
          if (!right.stamp) return -1;
          return left.stamp.localeCompare(right.stamp) * direction;
        default:
          return left.label.localeCompare(right.label, undefined, { numeric: true }) * direction;
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notes, sortKey, sortOrder]);

  const columns = [NOTE, VALUE, TYPE, ...(showSource ? [SOURCE] : []), UPDATED];

  const rows = sorted.map((note) => {
    const facts = factsOf(note);
    return {
      id: facts.id,
      [NOTE]: facts,
      [VALUE]: facts,
      [TYPE]: facts,
      [SOURCE]: facts,
      [UPDATED]: facts,
      /* The open note is marked on the row itself — `selectedIds` only drives
         the (absent) checkbox column. `Table` honours a row's own className. */
      className: facts.id && facts.id === selectedId ? 'bg-champagne/10' : undefined,
      _raw: note,
      _facts: facts,
    };
  });

  const muted = <span className="text-gray-400 dark:text-gray-500">—</span>;

  const renderCell = (column: string, value: any) => {
    const facts = value as ReturnType<typeof factsOf>;

    if (column === NOTE) {
      return (
        <span className="truncate font-medium text-fg-body" title={facts.label}>
          {facts.label}
        </span>
      );
    }

    if (column === VALUE) {
      if (!facts.display) return muted;
      return (
        <span className="block truncate text-fg-body" title={facts.display}>
          {facts.display}
        </span>
      );
    }

    if (column === TYPE) {
      return (
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-fg-muted">
          {TYPE_ICON[facts.type]}
          <span>{t(TYPE_LABEL[facts.type])}</span>
        </span>
      );
    }

    if (column === SOURCE) {
      if (facts.origin === 'ai') {
        return (
          <Badge color="amber" className="!text-[10px] !px-1.5 !py-0 whitespace-nowrap">
            <PiSparkleLight className="mr-1 inline h-3 w-3" aria-hidden />
            {t('AI generated')}
          </Badge>
        );
      }
      if (facts.origin === 'user') {
        return (
          <Badge color="gray" className="!text-[10px] !px-1.5 !py-0 whitespace-nowrap">
            <PiUserLight className="mr-1 inline h-3 w-3" aria-hidden />
            {facts.author || t('Added by us')}
          </Badge>
        );
      }
      return muted;
    }

    if (column === UPDATED) {
      return facts.stamp ? <span>{formatStamp(facts.stamp)}</span> : muted;
    }

    return undefined;
  };

  return (
    <Table
      tableId="matter-notes"
      columns={columns}
      data={rows}
      isLoading={isLoading}
      renderCell={renderCell}
      onRowClick={(row: any) => onSelectNote(row._raw)}
      sortableColumns={columns}
      sortBy={flatten(KEY_COLUMN[sortKey])}
      sortOrder={sortOrder}
      onSort={(flattenedColumn, order) => {
        setSortKey(COLUMN_KEY[flattenedColumn] || 'note');
        setSortOrder(order);
      }}
      /* The tab owns the height; the table fills what it is given and scrolls
         its rows internally. A `100vh-360px` cap left dead space below. */
      min="min-h-0"
      max="max-h-full"
      defaultColumnWidths={{
        [NOTE]: 240, [VALUE]: 400, [TYPE]: 130, [SOURCE]: 160, [UPDATED]: 140,
      }}
    />
  );
};

export default MatterNotesTable;
